/**
 * Class colours for the schedule calendar.
 *
 * A studio gets exactly as many colour options as it has classes, so every
 * class can own a distinct one. The first entries are a hand-picked set that
 * stays legible as a light card tint with darkened text on white; past that,
 * further hues are spread by the golden angle so they never bunch together.
 *
 * Picking a colour is optional. Classes without one are assigned the first
 * palette colour nobody has claimed, in creation order, so the calendar is
 * stable between visits and still never repeats a colour.
 */

// Each entry is a class's "dot" colour (legend, Day-view stripe, picker swatch).
// Cards use a pastel wash of it and its text a darker shade — the schedule
// design's formula (tint = 88% white, text = 25% black). The first three are
// that design's exact coral / purple / green.
const CURATED_PALETTE = [
  '#E8435A', // coral
  '#7A5CD1', // purple
  '#2B8C7E', // green
  '#E07B1F', // orange
  '#2F7FD8', // blue
  '#C2479B', // magenta
  '#1E9BB0', // cyan
  '#8A6A3D', // brown
  '#5C8F2E', // olive
  '#9B4FD1', // violet
  '#D9573A', // rust
  '#3E6FA8', // steel
  '#6B7480', // slate
  '#D1406E', // rose
  '#B8901A', // mustard
  '#5560D6', // indigo
];

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

export const isHexColor = (v: unknown): v is string => typeof v === 'string' && HEX_RE.test(v);

const hslToHex = (h: number, s: number, l: number): string => {
  const sat = s / 100;
  const light = l / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = sat * Math.min(light, 1 - light);
  const f = (n: number) => light - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const toHex = (x: number) => Math.round(x * 255).toString(16).padStart(2, '0');
  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`.toUpperCase();
};

/** `count` distinct colours (at least one). */
export const buildClassPalette = (count: number): string[] => {
  const n = Math.max(1, count);
  const palette = CURATED_PALETTE.slice(0, n);
  for (let i = palette.length; i < n; i++) {
    // Golden-angle hue walk, alternating lightness so neighbours differ more.
    const hue = (i * 137.508) % 360;
    palette.push(hslToHex(hue, 58, i % 2 ? 44 : 50));
  }
  return palette;
};

export interface ColorableClass {
  _id: string;
  className?: string;
  color?: string | null;
  createdAt?: string;
}

/**
 * classId -> colour for every class. Explicit picks win; the rest take the
 * first unclaimed palette colour in creation order.
 */
export const resolveClassColors = (classes: ColorableClass[]): Map<string, string> => {
  const palette = buildClassPalette(classes.length);
  const byId = new Map<string, string>();
  const taken = new Set<string>();

  classes.forEach((c) => {
    if (isHexColor(c.color)) {
      byId.set(c._id, c.color.toUpperCase());
      taken.add(c.color.toUpperCase());
    }
  });

  const unassigned = classes
    .filter((c) => !byId.has(c._id))
    .sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || '') || a._id.localeCompare(b._id));

  let cursor = 0;
  unassigned.forEach((c, i) => {
    while (cursor < palette.length && taken.has(palette[cursor])) cursor++;
    const color = cursor < palette.length ? palette[cursor] : palette[i % palette.length];
    taken.add(color);
    byId.set(c._id, color);
    cursor++;
  });

  return byId;
};

/** Fallback for a session whose class no longer exists: hash its name. */
export const colorForName = (name: string, palette: string[]): string => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
};

const mix = (hex: string, target: number, weight: number): string => {
  const n = parseInt(hex.slice(1), 16);
  const ch = (shift: number) => {
    const v = (n >> shift) & 0xff;
    return Math.round(v + (target - v) * weight).toString(16).padStart(2, '0');
  };
  return `#${ch(16)}${ch(8)}${ch(0)}`;
};

const luminance = (hex: string) => {
  const n = parseInt(hex.slice(1), 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
const contrast = (a: string, b: string) => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};

/**
 * Everything a calendar block needs from one class colour: the pastel card
 * wash, a slightly deeper hover, readable text and the dot itself.
 */
const tokenCache = new Map<string, ReturnType<typeof buildTokens>>();
const buildTokens = (hex: string) => {
  const bg = mix(hex, 255, 0.88);
  const bgHover = mix(hex, 255, 0.8);
  // 25% toward black, darkened further only if that shade would read below
  // 4.5:1 on the hover wash (the lighter oranges/mustards need it).
  let w = 0.25;
  let text = mix(hex, 0, w);
  while (contrast(text, bgHover) < 4.5 && w < 0.8) {
    w += 0.05;
    text = mix(hex, 0, w);
  }
  return { base: hex, bg, bgHover, border: hex, text, accent: hex };
};

export const classColorTokens = (hex: string) => {
  let t = tokenCache.get(hex);
  if (!t) {
    t = buildTokens(hex);
    tokenCache.set(hex, t);
  }
  return t;
};
